import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import useModuleHook from "../useModuleHook";
import GenericTierTwo from "../GenericTierTwo";
import GenericModule from "../GenericModule";

import type { ModuleFull } from "./largeFisheriesTypes";
import {
	getSchemas,
	validationSchema,
	initialValues,
	t2InitialValues,
} from "./largeFisheries";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useContextAwareGetLargeFisheriesDefaults,
	useContextAwareGetLargeFisheries,
	useUpdateLargeFisheriesMutation,
} from "@hooks/index";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import useErrorMessage from "@utils/useErrorMessage";

const LargeFisheriesModule = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);

	const id = moduleList.find(({ id }) => id === FEModules.LargeFisheries)?.uniqueId;

	const {
		data: largeFishData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetLargeFisheries(id ?? 0, { refetchOnMountOrArgChange: true });

	const { data: defaults, isError: isDefaultsError, error: defaultsError, isLoading: isLoadingDefaults } = useContextAwareGetLargeFisheriesDefaults(id ?? 0, { skip: !id });
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });

	const [
		updateLargeFishery,
		{ error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate },
	] = useUpdateLargeFisheriesMutation();

	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});

	const { tabsOpen, errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	useEffect(() => {
		if (largeFishData) {
			setInitValues({
				module: { ...largeFishData.module, ...defaults },
				tiertwo: { ...largeFishData.tiertwo, ...defaults },
			});
			processModuleThreads({
				module: largeFishData.module,
			})
		}
	}, [largeFishData, defaults, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const payload = {
			data: values,
			activityId: activeActivityId ?? 0,
		};

		try {
			values.module.id && (await updateLargeFishery(payload).unwrap());
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.large_fisheries"
					moduleSchema={moduleSchema}
					t2Schema={t2Schema}
					beModuleType={BEModulesClassNames.LargeFisheries}
					isLoading={isLoadingUpdate}
					footerError={errorMsg}
					blockingError={blockingError}
					isError={isUpdateError}
				/>
				{!tabsOpen ? (
					<GenericTierTwo
						title="project.large_fisheries"
						t2Schema={t2Schema}
						tabsOpen={tabsOpen}
						warningMessage={defaultsErrorMsg}
					/>
				) : null}
			</>
		</Formik>
	);
};

export default LargeFisheriesModule;
